import React from 'react'
import { styled, useTheme } from '@mui/material/styles'
import IconButton from '@mui/material/IconButton'
import Grid from '@mui/material/Grid'
import Divider from '@mui/material/Divider'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import Drawer from '@mui/material/Drawer'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import Collapse from '@mui/material/Collapse'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import SchemaIcon from '@mui/icons-material/Schema'
import GroupIcon from '@mui/icons-material/Group'
import LogoutIcon from '@mui/icons-material/Logout'
import Typography from '@mui/material/Typography'
import FeedIcon from '@mui/icons-material/Feed'
import TableViewIcon from '@mui/icons-material/TableView'
import MapIcon from '@mui/icons-material/Map'
import ArticleIcon from '@mui/icons-material/Article'
import EditCalendarIcon from '@mui/icons-material/EditCalendar'
// import GroupsIcon from '@mui/icons-material/Groups'
import PunchClockIcon from '@mui/icons-material/PunchClock'
import { ERole } from '@/store/apis/users'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { StyledRoot } from './navbarStyle'
import CodeIcon from '@mui/icons-material/Code'
import ListItem from './ListItem'

interface INavBar {
  drawerWidth: number
  isOpen: boolean
  toggleDrawerOpen(): void
  isMobileWidth: boolean
}

const Navbar: React.FC<INavBar> = ({
  drawerWidth,
  isOpen,
  toggleDrawerOpen,
  isMobileWidth,
}) => {
  const userRole = localStorage.getItem('userRole')
  const { t } = useTranslation()
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const names = location.pathname.split('/')
  const isInSchema = names.length > 3 && names[1] === 'schemas'
  const isInUserSchema = names.length > 3 && names[1] === 'schema'
  const [isUserSchemaExpandOpen, setIsUserSchemaExpandOpen] =
    React.useState(isInUserSchema)
  const [isSchemaExpandOpen, setIsSchemaExpandOpen] = React.useState(isInSchema)

  React.useEffect(() => {
    setIsUserSchemaExpandOpen(isInUserSchema)
  }, [isInUserSchema])

  React.useEffect(() => {
    setIsSchemaExpandOpen(isInSchema)
  }, [isInSchema])

  const logOut = () => {
    // TODO: Fix better way of handeling these
    localStorage.removeItem('accessToken')
    localStorage.removeItem('userId')
    localStorage.removeItem('userName')
    localStorage.removeItem('userRole')
    navigate('/')
  }

  const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // Necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
  }))

  return (
    <>
      <StyledRoot>
        <Drawer
          id="navbar-drawer"
          variant={isMobileWidth ? 'temporary' : 'persistent'}
          anchor="left"
          onClose={toggleDrawerOpen}
          open={isOpen}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
            },
          }}
        >
          <DrawerHeader>
            <Grid
              container
              display="flex"
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              textAlign="center"
            >
              <Grid
                item
                xs={isMobileWidth ? 9 : 12}
                container
                direction="column"
                justifyContent="center"
                alignItems="center"
                padding={2}
              >
                <Grid item xs>
                  {/* TODO: Fix me for long names */}
                  <img
                    src={'/images/teatersmedjan.svg'}
                    alt="teatersmedjan-logo"
                    width="100%"
                  />
                </Grid>
                <Grid item xs>
                  <Typography variant="subtitle1">
                    {`${t('baseLayout.welcome')} ${localStorage.getItem('userName')}`}
                  </Typography>
                </Grid>
              </Grid>
              {isMobileWidth && (
                <Grid item xs display="flex" justifyContent="flex-end">
                  <IconButton onClick={() => toggleDrawerOpen()}>
                    {theme.direction === 'ltr' ? (
                      <ChevronLeftIcon />
                    ) : (
                      <ChevronRightIcon />
                    )}
                  </IconButton>
                </Grid>
              )}
            </Grid>
          </DrawerHeader>
          <Divider />
          <List>
            <ListItemButton
              selected={names[1] === 'schema'}
              onClick={(event) => {
                event.stopPropagation()
                isMobileWidth && toggleDrawerOpen()
                navigate('/schema')
              }}
            >
              <ListItemIcon>
                <PunchClockIcon />
              </ListItemIcon>
              <ListItemText primary={t('baseLayout.schema')} />
              {isUserSchemaExpandOpen ? (
                <ExpandLess
                  onClick={(event) => {
                    event.stopPropagation()
                    setIsUserSchemaExpandOpen(false)
                  }}
                />
              ) : (
                <ExpandMore
                  onClick={(event) => {
                    event.stopPropagation()
                    setIsUserSchemaExpandOpen(true)
                  }}
                />
              )}
            </ListItemButton>
            <Collapse in={isUserSchemaExpandOpen} timeout="auto" unmountOnExit>
              <List disablePadding>
                <ListItem
                  isMobileWidth={isMobileWidth}
                  isSubItem={true}
                  selected={names[3] === 'schema'}
                  toggleDrawerOpen={toggleDrawerOpen}
                  onNavigate={() => navigate(`/schema/${names[2]}/schema`)}
                  icon={<PunchClockIcon />}
                  label={t('baseLayout.schema')}
                />
                <ListItem
                  isMobileWidth={isMobileWidth}
                  isSubItem={true}
                  selected={names[3] === 'created'}
                  toggleDrawerOpen={toggleDrawerOpen}
                  onNavigate={() => navigate(`/schema/${names[2]}/created`)}
                  icon={<PictureAsPdfIcon />}
                  label={t('baseLayout.createdSchemas')}
                />
                <ListItem
                  isMobileWidth={isMobileWidth}
                  isSubItem={true}
                  selected={names[3] === 'files'}
                  toggleDrawerOpen={toggleDrawerOpen}
                  onNavigate={() => navigate(`/schema/${names[2]}/files`)}
                  icon={<ArticleIcon />}
                  label={t('baseLayout.files')}
                />
              </List>
            </Collapse>
            {(userRole === ERole.SchemaCreator ||
              userRole === ERole.Admin ||
              userRole === ERole.Attendancer) && (
              <>
                {userRole !== ERole.Attendancer && (
                  <ListItem
                    isMobileWidth={isMobileWidth}
                    selected={names[1] === 'users'}
                    toggleDrawerOpen={toggleDrawerOpen}
                    onNavigate={() => navigate('/users')}
                    icon={<GroupIcon />}
                    label={t('baseLayout.users')}
                  />
                )}
                <ListItemButton
                  selected={names[1] === 'schemas'}
                  onClick={(event) => {
                    event.stopPropagation()
                    isMobileWidth && toggleDrawerOpen()
                    navigate('/schemas')
                  }}
                >
                  <ListItemIcon>
                    <SchemaIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('baseLayout.schemas')} />
                  {isSchemaExpandOpen ? (
                    <ExpandLess
                      onClick={(event) => {
                        event.stopPropagation()
                        setIsSchemaExpandOpen(false)
                      }}
                    />
                  ) : (
                    <ExpandMore
                      onClick={(event) => {
                        event.stopPropagation()
                        setIsSchemaExpandOpen(true)
                      }}
                    />
                  )}
                </ListItemButton>

                <Collapse in={isSchemaExpandOpen} timeout="auto" unmountOnExit>
                  <List disablePadding>
                    <ListItem
                      isMobileWidth={isMobileWidth}
                      isSubItem={true}
                      selected={names[3] === 'attendance'}
                      toggleDrawerOpen={toggleDrawerOpen}
                      onNavigate={() =>
                        navigate(`/schemas/${names[2]}/attendance`)
                      }
                      icon={<EditCalendarIcon />}
                      label={t('baseLayout.attendance')}
                      disabled={names.length < 3}
                      disableText={t('baseLayout.selectScehmaFirst')}
                    />
                    {userRole !== ERole.Attendancer && (
                      <>
                        <ListItem
                          isMobileWidth={isMobileWidth}
                          isSubItem={true}
                          selected={names[3] === 'users'}
                          toggleDrawerOpen={toggleDrawerOpen}
                          onNavigate={() =>
                            navigate(`/schemas/${names[2]}/users`)
                          }
                          icon={<GroupIcon />}
                          label={t('baseLayout.users')}
                          disabled={names.length < 3}
                          disableText={t('baseLayout.selectScehmaFirst')}
                        />
                        <ListItem
                          isMobileWidth={isMobileWidth}
                          isSubItem={true}
                          selected={names[3] === 'create'}
                          toggleDrawerOpen={toggleDrawerOpen}
                          onNavigate={() =>
                            navigate(`/schemas/${names[2]}/create`)
                          }
                          icon={<SchemaIcon />}
                          label={t('baseLayout.editSchema')}
                          disabled={names.length < 3}
                          disableText={t('baseLayout.selectScehmaFirst')}
                        />
                        <ListItem
                          isMobileWidth={isMobileWidth}
                          isSubItem={true}
                          selected={names[3] === 'places'}
                          toggleDrawerOpen={toggleDrawerOpen}
                          onNavigate={() =>
                            navigate(`/schemas/${names[2]}/places`)
                          }
                          icon={<MapIcon />}
                          label={t('baseLayout.places')}
                          disabled={names.length < 3}
                          disableText={t('baseLayout.selectScehmaFirst')}
                        />
                        <ListItem
                          isMobileWidth={isMobileWidth}
                          isSubItem={true}
                          selected={names[3] === 'scenes'}
                          toggleDrawerOpen={toggleDrawerOpen}
                          onNavigate={() =>
                            navigate(`/schemas/${names[2]}/scenes`)
                          }
                          icon={<FeedIcon />}
                          label={t('baseLayout.scenes')}
                          disabled={names.length < 3}
                          disableText={t('baseLayout.selectScehmaFirst')}
                        />
                        <ListItem
                          isMobileWidth={isMobileWidth}
                          isSubItem={true}
                          selected={names[3] === 'scene-groups'}
                          toggleDrawerOpen={toggleDrawerOpen}
                          onNavigate={() =>
                            navigate(`/schemas/${names[2]}/scene-groups`)
                          }
                          icon={<TableViewIcon />}
                          label={t('baseLayout.sceneGroups')}
                          disabled={names.length < 3}
                          disableText={t('baseLayout.selectScehmaFirst')}
                        />
                      </>
                    )}
                  </List>
                </Collapse>
              </>
            )}
            {userRole === ERole.Admin && (
              <ListItem
                isMobileWidth={isMobileWidth}
                selected={names[1] === 'api-docs'}
                toggleDrawerOpen={toggleDrawerOpen}
                onNavigate={() => navigate('/api-docs')}
                icon={<CodeIcon />}
                label={t('baseLayout.apiDocs')}
              />
            )}
          </List>

          <List style={{ marginTop: `auto` }}>
            <ListItemButton onClick={logOut}>
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary={t('baseLayout.logout')} />
            </ListItemButton>
          </List>
        </Drawer>
      </StyledRoot>
    </>
  )
}

export default Navbar
